import { useEffect, useMemo, useState } from 'preact/hooks'
import { contactDetailApi, type ContactActivityItemDto, type ContactAnchorType } from '@/api/contactDetail'
import { BRAND_CLASSES } from '@/constants/theme'
import { ActivityRail } from '../ActivityRail'

// Primary taxonomy chips. Server-side Kind values (Interaction/Web/Email) are unchanged — these are
// display labels only: Conversions → Interaction, Sessions → Web, Emails → Email.
type Filter = 'all' | 'conversions' | 'sessions' | 'emails'
// Secondary split shown while Sessions is active, keyed on the derived SessionKind field.
type SessionFilter = 'all' | 'website' | 'landing'

const FILTER_TO_KIND: Record<Exclude<Filter, 'all'>, ContactActivityItemDto['Kind']> = {
  conversions: 'Interaction',
  sessions: 'Web',
  emails: 'Email',
}

interface Props {
  workspaceId: string
  campaignId: string
  anchorType: ContactAnchorType
  anchorId: string
  isAudienceOnly: boolean
}

export function ContactActivityTab({ workspaceId, campaignId, anchorType, anchorId, isAudienceOnly }: Props) {
  const [items, setItems] = useState<ContactActivityItemDto[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<Filter>('all')
  const [sessionFilter, setSessionFilter] = useState<SessionFilter>('all')

  useEffect(() => {
    const ctrl = new AbortController()
    setLoading(true)
    setError(null)
    contactDetailApi.getActivity(workspaceId, campaignId, anchorType, anchorId, ctrl.signal)
      .then(res => {
        if (ctrl.signal.aborted) return
        if (res.data) setItems(res.data.Items)
        else setError(res.error || 'Failed to load activity')
      })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false) })
    return () => ctrl.abort()
  }, [workspaceId, campaignId, anchorType, anchorId])

  const filtered = useMemo(() => {
    if (!items) return []
    if (filter === 'all') return items
    const byKind = items.filter(i => i.Kind === FILTER_TO_KIND[filter])
    if (filter !== 'sessions' || sessionFilter === 'all') return byKind
    // Landing page = SessionKind === 'Landing page'; Website = everything else (incl. null/absent).
    return byKind.filter(i =>
      sessionFilter === 'landing' ? i.SessionKind === 'Landing page' : i.SessionKind !== 'Landing page'
    )
  }, [items, filter, sessionFilter])

  if (loading && !items) {
    return <div class="px-6 py-10 text-sm text-slate-500 dark:text-slate-400">Loading activity…</div>
  }
  if (error && !items) return <div class="px-6 py-10 text-sm text-red-600 dark:text-red-400">{error}</div>
  if (!items || items.length === 0) {
    return (
      <div class="px-6 py-16 text-center">
        <p class="text-sm font-medium text-slate-700 dark:text-slate-200">No activity yet</p>
        <p class="mx-auto mt-1 max-w-md text-sm text-slate-500 dark:text-slate-400">
          {isAudienceOnly
            ? 'Conversions, sessions, and email events will stream here as they happen.'
            : 'This contact has no recorded conversions, sessions, or email events yet.'}
        </p>
      </div>
    )
  }

  const chips: Array<{ key: Filter; label: string }> = [
    { key: 'all', label: 'All' },
    { key: 'conversions', label: 'Conversions' },
    { key: 'sessions', label: 'Sessions' },
    { key: 'emails', label: 'Emails' },
  ]

  const sessionChips: Array<{ key: SessionFilter; label: string }> = [
    { key: 'all', label: 'All' },
    { key: 'landing', label: 'Landing pages' },
    { key: 'website', label: 'Websites' },
  ]

  const chipClass = (active: boolean) =>
    `rounded-full px-3 py-1 text-xs font-medium ${active
      ? BRAND_CLASSES.BUTTON.PRIMARY
      : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'}`

  // Empty-copy tailored to the active sub-filter so an empty Sessions split reads clearly.
  const emptyMessage = filter === 'sessions' && sessionFilter === 'website'
    ? 'No website sessions yet'
    : filter === 'sessions' && sessionFilter === 'landing'
      ? 'No landing page sessions yet'
      : 'No matching activity.'

  return (
    <div class="px-6 py-5">
      <div class="mb-3 flex flex-wrap items-center gap-2">
        {chips.map(c => (
          <button
            key={c.key}
            type="button"
            onClick={() => setFilter(c.key)}
            class={chipClass(filter === c.key)}
          >
            {c.label}
          </button>
        ))}
        <span class="ml-auto text-xs text-slate-400 dark:text-slate-500">{items.length} event{items.length === 1 ? '' : 's'}</span>
      </div>
      {filter === 'sessions' && (
        <div class="mb-4 flex flex-wrap items-center gap-2 pl-1">
          {sessionChips.map(c => (
            <button
              key={c.key}
              type="button"
              onClick={() => setSessionFilter(c.key)}
              class={chipClass(sessionFilter === c.key)}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}
      {filtered.length > 0
        ? <ActivityRail
            items={filtered}
            showCampaignTag
            suppressKindBadge={filter === 'all' ? undefined : FILTER_TO_KIND[filter]}
            showSessionSubtype={!(filter === 'sessions' && sessionFilter !== 'all')}
          />
        : <p class="text-sm text-slate-500 dark:text-slate-400">{emptyMessage}</p>}
    </div>
  )
}
